/**
 * Cookie Policy Page
 * GDPR compliant cookie policy for Artego Condo Manager
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Cookie, Shield, CheckCircle2, XCircle, Settings } from 'lucide-react';

export default function CookiePolicy() {
  return (
    <div className="artego-prose min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-5 w-5" />
            <span>Torna alla Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Cookie className="h-6 w-6 text-amber-600" />
            <span className="font-semibold text-slate-900">ARTEGO.APP</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg">
          <CardContent className="p-6 lg:p-10">
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <Cookie className="h-8 w-8 text-amber-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Cookie Policy</h1>
              <p className="text-slate-600">Informativa sull'utilizzo dei cookie</p>
              <p className="text-sm text-slate-500 mt-2">Ultimo aggiornamento: Gennaio 2026</p>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              
              {/* Introduction */}
              <section className="mb-8">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800">
                    <strong>ARTEGO.APP</strong> utilizza cookie e tecnologie simili per garantire il corretto 
                    funzionamento del sito, migliorare l'esperienza utente e, con il tuo consenso, 
                    analizzare il traffico e personalizzare i contenuti.
                  </p>
                </div>
              </section>

              {/* Section 1 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                  Cosa sono i Cookie?
                </h2>
                <p className="text-slate-700 mb-4">
                  I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo 
                  (computer, tablet, smartphone) quando visiti un sito web. Servono a ricordare le tue 
                  preferenze, migliorare la navigazione e raccogliere informazioni statistiche.
                </p>
                <p className="text-slate-700">
                  I cookie possono essere:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-2">
                  <li><strong>Cookie di prima parte:</strong> impostati direttamente da ARTEGO.APP</li>
                  <li><strong>Cookie di terze parti:</strong> impostati da servizi esterni (es. analytics)</li>
                  <li><strong>Cookie di sessione:</strong> eliminati alla chiusura del browser</li>
                  <li><strong>Cookie persistenti:</strong> rimangono per un periodo definito</li>
                </ul>
              </section>

              {/* Section 2 - Cookie Types */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                  Tipologie di Cookie Utilizzati
                </h2>

                {/* Necessary Cookies */}
                <div className="border rounded-lg overflow-hidden mb-4">
                  <div className="bg-green-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-slate-900">Cookie Necessari</h3>
                    </div>
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">Sempre attivi</span>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-slate-700 text-sm mb-3">
                      Questi cookie sono essenziali per il funzionamento del sito e non possono essere disattivati. 
                      Vengono impostati in risposta ad azioni effettuate dall'utente, come l'impostazione delle 
                      preferenze privacy, l'accesso all'area riservata o la compilazione di moduli.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border border-slate-300 px-3 py-2 text-left">Nome</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Scopo</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Durata</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-600">
                          <tr>
                            <td className="border border-slate-300 px-3 py-2 font-mono">token</td>
                            <td className="border border-slate-300 px-3 py-2">Autenticazione utente</td>
                            <td className="border border-slate-300 px-3 py-2">Sessione</td>
                          </tr>
                          <tr className="bg-slate-50">
                            <td className="border border-slate-300 px-3 py-2 font-mono">artego_cookie_consent</td>
                            <td className="border border-slate-300 px-3 py-2">Memorizza preferenze cookie</td>
                            <td className="border border-slate-300 px-3 py-2">1 anno</td>
                          </tr>
                          <tr>
                            <td className="border border-slate-300 px-3 py-2 font-mono">workspace_id</td>
                            <td className="border border-slate-300 px-3 py-2">Identificazione workspace attivo</td>
                            <td className="border border-slate-300 px-3 py-2">Sessione</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border rounded-lg overflow-hidden mb-4">
                  <div className="bg-blue-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Settings className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold text-slate-900">Cookie Analitici</h3>
                    </div>
                    <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">Opzionali</span>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-slate-700 text-sm mb-3">
                      Questi cookie ci permettono di contare le visite e le fonti di traffico per misurare 
                      e migliorare le prestazioni del sito. Ci aiutano a sapere quali pagine sono più o meno 
                      popolari e come gli utenti navigano nel sito.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border border-slate-300 px-3 py-2 text-left">Nome</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Provider</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Scopo</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Durata</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-600">
                          <tr>
                            <td className="border border-slate-300 px-3 py-2 font-mono">_ga</td>
                            <td className="border border-slate-300 px-3 py-2">Google Analytics</td>
                            <td className="border border-slate-300 px-3 py-2">Distinguere utenti</td>
                            <td className="border border-slate-300 px-3 py-2">2 anni</td>
                          </tr>
                          <tr className="bg-slate-50">
                            <td className="border border-slate-300 px-3 py-2 font-mono">_gid</td>
                            <td className="border border-slate-300 px-3 py-2">Google Analytics</td>
                            <td className="border border-slate-300 px-3 py-2">Distinguere utenti</td>
                            <td className="border border-slate-300 px-3 py-2">24 ore</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Preference Cookies */}
                <div className="border rounded-lg overflow-hidden mb-4">
                  <div className="bg-purple-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Settings className="h-5 w-5 text-purple-600" />
                      <h3 className="font-semibold text-slate-900">Cookie di Preferenze</h3>
                    </div>
                    <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded">Opzionali</span>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-slate-700 text-sm mb-3">
                      Questi cookie permettono al sito di ricordare le scelte effettuate (come la lingua, 
                      la regione o altre personalizzazioni) per offrire una navigazione più confortevole.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="border border-slate-300 px-3 py-2 text-left">Nome</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Scopo</th>
                            <th className="border border-slate-300 px-3 py-2 text-left">Durata</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-600">
                          <tr>
                            <td className="border border-slate-300 px-3 py-2 font-mono">artego_theme</td>
                            <td className="border border-slate-300 px-3 py-2">Preferenze tema (chiaro/scuro)</td>
                            <td className="border border-slate-300 px-3 py-2">1 anno</td>
                          </tr>
                          <tr className="bg-slate-50">
                            <td className="border border-slate-300 px-3 py-2 font-mono">artego_sidebar</td>
                            <td className="border border-slate-300 px-3 py-2">Stato sidebar (aperta/chiusa)</td>
                            <td className="border border-slate-300 px-3 py-2">1 anno</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <h3 className="font-semibold text-slate-900">Cookie di Marketing</h3>
                    </div>
                    <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">Opzionali</span>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-slate-700 text-sm mb-3">
                      Questi cookie vengono utilizzati per tracciare i visitatori sui siti web e mostrare 
                      annunci pertinenti e coinvolgenti per il singolo utente. Attualmente ARTEGO.APP 
                      <strong> non utilizza cookie di marketing</strong>.
                    </p>
                    <div className="bg-slate-50 rounded p-3 text-sm text-slate-600 italic">
                      Nessun cookie di marketing attivo
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                  Come Gestire i Cookie
                </h2>
                <p className="text-slate-700 mb-4">
                  Puoi gestire le tue preferenze sui cookie in diversi modi:
                </p>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-amber-500 pl-4">
                    <h3 className="font-semibold text-slate-900">Banner Cookie</h3>
                    <p className="text-slate-600 text-sm">
                      Al primo accesso al sito, ti verrà mostrato un banner che ti permette di accettare 
                      tutti i cookie, solo quelli necessari, o personalizzare le tue preferenze.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="font-semibold text-slate-900">Impostazioni del Browser</h3>
                    <p className="text-slate-600 text-sm">
                      Puoi modificare le impostazioni del tuo browser per bloccare o eliminare i cookie. 
                      Consulta la guida del tuo browser per le istruzioni specifiche:
                    </p>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1">
                      <li>• <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Chrome</a></li>
                      <li>• <a href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Mozilla Firefox</a></li>
                      <li>• <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Apple Safari</a></li>
                      <li>• <a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Microsoft Edge</a></li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-semibold text-slate-900">Strumenti di Opt-Out</h3>
                    <p className="text-slate-600 text-sm">
                      Per i cookie di terze parti, puoi utilizzare gli strumenti di opt-out forniti 
                      direttamente dai provider:
                    </p>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1">
                      <li>• <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Opt-Out</a></li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Nota:</strong> La disabilitazione dei cookie necessari potrebbe compromettere 
                    il corretto funzionamento del sito e impedire l'accesso ad alcune funzionalità.
                  </p>
                </div>
              </section>

              {/* Section 4 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                  Base Giuridica
                </h2>
                <p className="text-slate-700">
                  La base giuridica per l'utilizzo dei cookie è:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-2">
                  <li><strong>Cookie necessari:</strong> legittimo interesse del Titolare a garantire il funzionamento del sito (Art. 6.1.f GDPR)</li>
                  <li><strong>Cookie analitici, di preferenze e marketing:</strong> consenso dell'utente (Art. 6.1.a GDPR e Art. 122 D.Lgs. 196/2003)</li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                  Titolare del Trattamento
                </h2>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="mb-2"><strong>ARTEGO SRLS</strong></p>
                  <p className="text-slate-600 text-sm">
                    Via M.se di Villabianca, 114<br />
                    90143 Palermo (PA) - Italia<br />
                    Email: <a href="mailto:privacy@artegosrls.it" className="text-blue-600 hover:underline">privacy@artegosrls.it</a><br />
                    PEC: <a href="mailto:artegosrls@pec.it" className="text-blue-600 hover:underline">artegosrls@pec.it</a>
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                  Aggiornamenti
                </h2>
                <p className="text-slate-700">
                  La presente Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultare 
                  questa pagina regolarmente per essere informato su eventuali modifiche. La data dell'ultimo 
                  aggiornamento è indicata in cima alla pagina.
                </p>
              </section>

            </div>

            {/* Footer Actions */}
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Cookie className="h-5 w-5" />
                <span className="text-sm">Documento aggiornato a Gennaio 2026</span>
              </div>
              <div className="flex gap-3">
                <Link to="/privacy">
                  <Button variant="outline">
                    <Shield className="h-4 w-4 mr-2" />
                    Privacy Policy
                  </Button>
                </Link>
                <Link to="/login">
                  <Button>Accedi</Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2026 Artego SRLS - Tutti i diritti riservati</p>
          <p className="mt-1">Via M.se di Villabianca, 114 - 90143 Palermo</p>
        </div>
      </footer>
    </div>
  );
}
